import {
  isWhatsAppTemplateStatus,
  type WhatsAppTemplateStatus,
} from "@hightouch/lib/cep/types/whatsapp-template";

import type { CepWhatsAppTemplateRowFragment } from "src/graphql";

/** Meta's verdict on a submission. */
export type WhatsAppTemplateApproval = "pending" | "approved" | "rejected";

/**
 * What the Status column shows for a template.
 *
 * Two axes collapsed into one column: a Hightouch lifecycle,
 * and Meta's approval state while the template is still a draft.
 */
export type WhatsAppTemplateStatusDisplay = {
  lifecycle: "draft" | "active" | "paused" | "disabled";
  /** Rendered beside the lifecycle badge. Null renders nothing there. */
  annotation: WhatsAppTemplateApproval | null;
};

/** Meta's approval isn't required here; it's checked when the message goes live. */
export function isPickableLibraryTemplate(
  row:
    | Pick<CepWhatsAppTemplateRowFragment, "added_to_library" | "archived_at">
    | null
    | undefined,
): boolean {
  return Boolean(row?.added_to_library) && row?.archived_at == null;
}

/**
 * A template carries one locale row per language, and only English exists
 * today. So we only look at the first row for now.
 */
export function primaryLocale(
  row: Pick<CepWhatsAppTemplateRowFragment, "cep_whatsapp_template_locales">,
) {
  return row.cep_whatsapp_template_locales[0];
}

/**
 * Narrows a status column at the point it is read. Meta can report a value we
 * have not mapped, and `null` is a template row that has not been written yet;
 * both come back as null so the caller cannot mistake one for a live state.
 */
export function readWhatsAppTemplateStatus(
  status: string | null | undefined,
): WhatsAppTemplateStatus | null {
  if (!status || !isWhatsAppTemplateStatus(status)) return null;
  return status;
}

export type WhatsAppTemplateStatusInput = {
  /** Meta's status for the template's language, or ours before submission. */
  status: WhatsAppTemplateStatus | null;
  addedToLibrary: boolean;
  /** Distinct messages whose content sends this template. */
  usageCount: number;
};

/**
 * A template is Active only once all three hold: Meta approved it, it was
 * added to the library, and a message actually sends it. Approved but unused
 * stays a draft.
 */
export function deriveWhatsAppTemplateStatus({
  status,
  addedToLibrary,
  usageCount,
}: WhatsAppTemplateStatusInput): WhatsAppTemplateStatusDisplay {
  const block = readWhatsAppTemplateBlock(status);
  if (block) return { lifecycle: block, annotation: null };
  const approval = readWhatsAppTemplateApproval(status);
  if (approval === "approved" && addedToLibrary && usageCount > 0) {
    return { lifecycle: "active", annotation: null };
  }
  return { lifecycle: "draft", annotation: approval };
}

/** Whether Meta is holding back sends: paused until it lifts, or disabled for good. */
export function readWhatsAppTemplateBlock(
  status: WhatsAppTemplateStatus | null,
): Extract<
  WhatsAppTemplateStatusDisplay["lifecycle"],
  "paused" | "disabled"
> | null {
  switch (status) {
    case "PAUSED":
    case "LOCKED":
    case "LIMIT_EXCEEDED":
      return "paused";
    case "DISABLED":
    case "DELETED":
    case "PENDING_DELETION":
      return "disabled";
    default:
      return null;
  }
}

/** Meta's verdict on its own, for surfaces that show their own lifecycle. */
export function readWhatsAppTemplateApproval(
  status: WhatsAppTemplateStatus | null,
): WhatsAppTemplateApproval | null {
  switch (status) {
    case "APPROVED":
    // REINSTATED restores a template Meta had suppressed, and FLAGGED is a
    // quality warning that does not stop delivery, so both still send.
    case "REINSTATED":
    case "FLAGGED":
      return "approved";
    case "PENDING":
    // An appeal of a rejection is awaiting a verdict, like a fresh review.
    case "IN_APPEAL":
      return "pending";
    case "REJECTED":
      return "rejected";
    default:
      // UNSUBMITTED, and any status Meta adds that we have not mapped: a
      // template we cannot place is not one we should call live.
      return null;
  }
}

export const WHATSAPP_TEMPLATE_LIFECYCLE_LABELS: Record<
  WhatsAppTemplateStatusDisplay["lifecycle"],
  string
> = {
  draft: "Draft",
  active: "Active",
  paused: "Paused",
  disabled: "Disabled",
};

export const WHATSAPP_TEMPLATE_APPROVAL_LABELS: Record<
  WhatsAppTemplateApproval,
  string
> = {
  pending: "Pending approval",
  approved: "Approved",
  rejected: "Rejected",
};

export type TemplateEditability = {
  canEditName: boolean;
  canEditCategory: boolean;
  canEditContent: boolean;
  canChangeAccount: boolean;
};

/**
 * What a template still allows, as one table rather than four conditions
 * spread through the page.
 *
 * Two freezes, at different moments and for different reasons. Content
 * existing in Twilio binds the account — a minted SID counts, not just a
 * submitted one — because Meta holds the template on that WABA and moving ours
 * would strand it. An approved template in use binds everything, because its
 * sends depend on the copy Meta approved. Otherwise content stays editable, and
 * an edit discards the submission once the marketer confirms it.
 *
 * A replacement in flight also blocks editing, but the server owns that: it
 * lasts one provider call, and the refusal carries the reason.
 */
export function templateEditability({
  approvedAt,
  submittedAt,
  canManageAssets,
  providerContentId,
  isFrozen,
}: {
  approvedAt: string | null | undefined;
  submittedAt: string | null | undefined;
  canManageAssets: boolean;
  providerContentId?: string | null;
  isFrozen: boolean;
}): TemplateEditability {
  const isApproved = Boolean(approvedAt);
  // Approval implies submission, but the data is provider-reported, so this
  // does not assume the two arrive together.
  const isSubmitted =
    Boolean(submittedAt) || isApproved || Boolean(providerContentId);
  const canEdit = canManageAssets && !isFrozen;

  return {
    canEditName: canEdit,
    canEditCategory: canEdit,
    canEditContent: canEdit,
    canChangeAccount: canEdit && !isSubmitted,
  };
}

export type WhatsAppTemplateQuality = "high" | "medium" | "low";

export function readWhatsAppTemplateQuality(
  quality: string | null | undefined,
): WhatsAppTemplateQuality | null {
  switch (quality) {
    case "GREEN":
      return "high";
    case "YELLOW":
      return "medium";
    case "RED":
      return "low";
    default:
      return null;
  }
}
